
import { Component, Prop, Vue, Watch, Emit } from "vue-property-decorator";
import { Getter, namespace } from "vuex-class";
import vSelect3 from "vselect3";
import axios, { CancelTokenSource } from "axios";
import { debounce, differenceBy, isEqual, unionWith } from "lodash";
import api from "@/services/api.service";
import { UserInfo, UserPermissions, UserTypeFilterEnum, VueSelectDropdownProperties } from "@/store/types";
import { AddInvolvedPartyRequest, InvolvedParty, RemoveInvolvedPartyRequest } from "@/store/site-monitor/types";
import { truncateString } from "@/filters";
import { userMixins} from "@/mixins";
import { Modules } from "@/store/ModuleTypes";
import { NotificationOptionsType } from "@/types/NotificationOptionsType";
import { MaxLength } from "@/types/sv-data/users/Constants";
import involvedPartiesApi from "@/services/api.involvedParties";
import AnonymizeInvolvedParty from "./AnonymizeInvolvedParty.vue";
import { maxLength } from "vuelidate/lib/validators";

const SiteMonitor = namespace(Modules.SiteMonitor);

const { formattedUserTitle } = userMixins.methods;

@Component({
    components: {
        "v-select-3": vSelect3,
		"anonymize-involved-party": AnonymizeInvolvedParty
    },
	filters: { truncateString }
})
export default class InvolvedParties extends Vue {
    @Prop(Number)
    eventId?: number;

	$refs!: {
		involvedPartiesModal: any;
	};

	private readonly debouncePeriod: number = process.env.NODE_ENV === "test" ? 1 : 500;
	private readonly maxUserLimit: number = 100;
	private readonly maxWidth: string = '50vw';
	private readonly maxTruncatingCharacters: number = 30;
	private readonly maxLengthFullName: number = MaxLength.FULL_NAME; // Define maximum length

	private readonly noContactsFound: string = 'No contacts were found';
	private readonly noUsersFound: string = 'No users or contacts were found';
	private readonly adHocText: string = '[Ad Hoc]';
	private readonly addedText: string = '(Added)';

    private selectedParties: UserInfo[] = [];
	private involvedPartySearchToken: CancelTokenSource;
	private users: UserInfo[] = [];
	private involvementType: string | null = null;
	private includeUsers: boolean = false;
	private currentSearchTerm: string = "";

	@Getter("getPermissions") permissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

    @SiteMonitor.Getter("getInvolvedPartiesShown") involvedPartiesShown!: boolean;

    @SiteMonitor.Mutation setInvolvedPartiesShown: (shown: boolean) => void;
	@SiteMonitor.Mutation private resetEventRecords: () => void;
    @SiteMonitor.State private involvedParties: InvolvedParty[];
    @SiteMonitor.Action private loadInvolvedPartiesForEvent: (eventId: number) => Promise<void>;

	public created(): void {
        this.setInvolvedPartiesShown(false);
		if (!this.excludeUsersEnabled) {
			this.includeUsers = true;
		}
    }

	public destroy(): void {
		this.currentSearchTerm = "";
	}

    @Watch("involvedPartiesShown")
    private async onInvolvedPartiesShownChanged(value: boolean): Promise<void> {
        if (value) {
			await this.updateInvolvedParties();
			this.clearInputs();
            this.$refs.involvedPartiesModal.show();
        } else {
            this.$refs.involvedPartiesModal.hide();
        }
    }

	private get canEditInvolvedParties(): boolean {
		return !!this.permissions.canEditInvolvedParties;
	}

	private get canAddAdhocInvolvedParties(): boolean {
		return !!this.permissions.canAddInvolvedPartyAdHoc;
	}

	private get isValid(): boolean {
		if (this.selectedParties.length == 0)
		{
			return false;
		}

		if (!this.involvementType || this.involvementType.trim() === "")
		{
			return false;
		}

		return true;
	}

	private get excludeUsersEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "InvolvedParties","ExcludeUsers"]);
	}

	private get addAdhocEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "InvolvedParties","AddAdHoc"]);
	}

	public get isAnonymizeInvolvedPartiesEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "InvolvedParties", "AnonymizeInvolvedParties"]) && this.permissions.canAnonymizeInvolvedParties;
	}

    public closeInvolvedParties(): void {
		this.users = [];
        this.setInvolvedPartiesShown(false);
    }

	private async handleIncludeUsersChange(): Promise<void> {
		this.users = [];
		await this.performInvolvedPartySearch(this.currentSearchTerm);
	}

	private async handleInvolvedPartySearchInput(searchTerm: string): Promise<void> {
		let truncatedTerm: string = searchTerm;

		// Truncate the search term if it exceeds maximum length
		if (searchTerm.length > this.maxLengthFullName) {
			truncatedTerm = searchTerm.trim().substring(0, this.maxLengthFullName);
		}

		this.currentSearchTerm = truncatedTerm;
		await this.performInvolvedPartySearch(truncatedTerm);
	}

	/**
	 * Creates a placeholder object for a manually added ad hoc involved party name.
	 * @param {string} newOption - The new involved party full name.
	 * @returns {UserInfo}
	 */
	@Emit("option:created")
	 private createOption(newOption: string): UserInfo {
		let fullName = newOption.trim();

		if (newOption.length > this.maxLengthFullName) {
			fullName = newOption.substring(0, this.maxLengthFullName);
		}

		const adhocInvolvedParty : UserInfo = {
			userId : null,
			fullName: fullName,
			email: null,
			notes: null
		};

		return adhocInvolvedParty;
	}

	private performInvolvedPartySearch: (searchTeam: string) => Promise<void> = debounce(
		async function (searchTerm: string) {
			if (this.involvedPartySearchToken) {
				this.involvedPartySearchToken.cancel("Cancel Search");
			}

			this.involvedPartySearchToken = axios.CancelToken.source();

			try {
				this.users = [];
				let usersInfo = await api.getUsersInfoBySearchTerm(
					searchTerm,
					this.includeUsers ? UserTypeFilterEnum.UsersAndContacts : UserTypeFilterEnum.ContactsOnly,
					this.maxUserLimit);
				this.users = unionWith(usersInfo, this.selectedParties, isEqual);

				if (!this.allowAddAdHocInvolvedParty) {
					// get only the Involved parties that are added and are users or contacts in the system.
					let existingInvolvedParties = this.involvedParties.filter(ip => ip.added && !this.isUserIdNull(ip.userId));
					this.users = differenceBy(this.users, existingInvolvedParties, 'userId');
				}
			} catch (e) {
				return; //Return when past requests are cancelled
			}
		},
		this.debouncePeriod,
		{ leading: false, trailing: true }
	);

	private async addInvolvedParty(): Promise<void> {
		let involvedPartiesToAdd: AddInvolvedPartyRequest[] = [];

		this.selectedParties.forEach(involvedParty => {
			involvedPartiesToAdd.push({
				eventId: this.eventId,
				userId: involvedParty.userId,
				fullName: involvedParty.fullName,
				involvementType: this.involvementType
				} as AddInvolvedPartyRequest)
		});

		for (var i = 0; i < involvedPartiesToAdd.length; i++) {
			await involvedPartiesApi.addInvolvedParty(involvedPartiesToAdd[i]);
		}

		this.selectedParties = [];
		this.users = [];
		this.involvementType = null;

		await this.updateInvolvedParties();
	}

	private async updateInvolvedParties(): Promise<void> {
		try {
			await this.loadInvolvedPartiesForEvent(this.eventId);
		} catch (err) {
			console.error("Unable to fetch Involved Parties for Event", err);
		}
	}

	private clearInputs(): void {
		this.selectedParties = [];
		this.involvementType = null;
	}

	private async removeInvolvedParty(involvedParty: InvolvedParty): Promise<void> {
		const removeRequest: RemoveInvolvedPartyRequest = {
			eventId: this.eventId,
			userId: involvedParty.userId,
			fullName: involvedParty.fullName,
			involvedPartyId: involvedParty.involvedPartyId
		};

		await involvedPartiesApi.removeInvolvedParty(removeRequest);
		await this.updateInvolvedParties();
	}

	private calculateDropdownPositionAndSize(dropdownList: HTMLUListElement, component: Vue, { width, top, left }: VueSelectDropdownProperties): void {
        dropdownList.style.top = top;
        dropdownList.style.left = left;
        dropdownList.style.width = 'auto';
		dropdownList.style.minWidth = width;
		dropdownList.style.maxWidth = this.maxWidth;
    }

	private getUserTitle(user: UserInfo): string {
		return formattedUserTitle(user);
	}

	/**
	 * To determine if the No user or contacts text to be shown when adding Adhoc involved party.
	 * @param {UserInfo} option - The new involved party full name.
	 * @returns {boolean}
	 */
	private showNoUsersFoundText(option: UserInfo) : boolean {
		// users does not have a user or contact, ignore adhoc entries count
		// and fullName is not in the select list.
		if (this.users.filter(u => !this.isUserIdNull(u.userId)).length == 0 &&
			this.selectedParties.findIndex(u => u.fullName === option.fullName) < 0) {
				return true;
			}
		return false;
	}

	private isExistingInvolvedParty(option: UserInfo): boolean {
		if (!this.isUserIdNull(option.userId)) {
			return this.involvedParties.findIndex(ip => ip.added &&	ip.userId === option.userId) >= 0;
		}
		else {
			return this.involvedParties.findIndex(ip => ip.added &&
					this.isUserIdNull(ip.userId) &&
					(ip.fullName?.toLowerCase() === option.fullName?.toLowerCase())) >= 0;
		}
	}

	private get allowAddAdHocInvolvedParty(): boolean {
		return this.addAdhocEnabled && this.canAddAdhocInvolvedParties;
	}

	private handleSelectionIfAllowed(selectedOption: UserInfo[]): void {
		// Only process the selection if add-adhoc is enabled
		if (this.allowAddAdHocInvolvedParty) {
			this.handleSelected(selectedOption);
		}
	}

	private handleSelected(selectedOption: UserInfo[]): void {
		const maxInvolvedPartiesWithFullName: number = 2;

		// get the selected option - newest will be last in the array
		let option = selectedOption.slice(-1)[0];

		const matchingInvolvedPartiesCount = this.involvedParties.filter(ip => ip.added && ip.userId == option.userId && ip.fullName?.toLowerCase() === option.fullName.toLowerCase()).length;
		let errorMessage = `"${option.fullName}" is already an involved party`;

		if (this.isUserIdNull(option.userId)) {
			errorMessage = `${errorMessage} added via ad hoc`;
		}

		if (matchingInvolvedPartiesCount !== 0) {
			this.removedOptionFromSelectedParties(option);

			this.$notify({
				type: NotificationOptionsType.error,
				title: "Cannot Select",
				text: errorMessage
			});
		}
	}

	private removedOptionFromSelectedParties(option: UserInfo): void {
		// Find the index of the option that was just added
		const index = this.selectedParties.findIndex(p => p.userId === option.userId && p.fullName === option.fullName);

		// Remove it if found
		if (index >= 0) {
			this.selectedParties.splice(index, 1);
		}

	}

	private optionIsAlreadySelected(option: UserInfo) : boolean {
		return this.selectedParties.findIndex(sp => sp.userId === option.userId && sp.fullName?.toLowerCase().trim() === option.fullName?.toLowerCase().trim()) >= 0;
	}

	private isUserIdNull(userId: number | null): boolean {
		return userId === null;
	}

	private isOptionSelectable(option: UserInfo): boolean {
		// Don't allow selection of options that are already involved parties or already selected
		if (this.isExistingInvolvedParty(option) || this.optionIsAlreadySelected(option)) {
			return false;
		}

		return true;
	}

	 /**
	* badgeColours.css contains user-1 through user-10 as badge colour classes
    * This method cycles through the ten classes, wrapping back if exceeding the max
	*/
    private userColourIndex(index: number): number {
        return (index % 10) + 1;
    }

	private async anonymiseInvolvedParty(involvedPartyId: number): Promise<void> {
		await involvedPartiesApi.anonymizeInvolvedParty(involvedPartyId);
		await this.updateInvolvedParties();
		this.resetEventRecords();
	}

	private canAnonymizeInvolvedParty(involvedParty: InvolvedParty): boolean {
		return !involvedParty.anonymized;
	}
}
